import { supabase } from '../libs/api';
import { encryptWithPublicKey } from './encrypt.js';
import { toStoredConsumerEan } from './gtinUtils.js';

const getFileUploadHistory = async (cdsId) => {
  const { data, error } = await supabase
    .schema('frontend')
    .rpc('get_customer_file_uploads_v2', { _connection_data_source_id: cdsId });

  if (error) {
    console.error('Error getting customer file history:', error);
  }

  return { data, error };
};

const getPreSignedS3Url = async (cdsId, fileName) => {
  const { data, error } = await supabase
    .schema('frontend')
    .rpc('get_next_upload_filepath', {
      _connection_data_source_id: cdsId,
      _file_name: fileName,
    });

  if (error) {
    console.error('Error getting pre-signed url:', error);
  }

  return { data, error };
};

const getSubscription = async () => {
  const { data, error } = await supabase
    .schema('frontend')
    .rpc('get_subscription');

  if (error) {
    console.error('Error getting subscription:', error);
    return;
  }

  return data;
};

const updateDefaultOrg = async (orgId) => {
  const { data, error } = await supabase
    .schema('frontend')
    .rpc('update_default_org_id', { _org_id: orgId });

  if (error) {
    console.error('Error updating default org id:', error);
    return;
  }

  return data;
};

const getOrganizationProducts = async (organization_id) => {
  const { data, error } = await supabase.schema('frontend').rpc('get_organization_products_v4', {
    _organization_id: organization_id,
  });

  if (error) {
    console.error('Error getting org products info:', error);
    return;
  }

  return data;
};

const createProductAndCasepack = async (organization_id, product_data) => {
  const { data, error } = await supabase.schema('frontend').rpc('create_product_and_casepack_v3', {
    _organization_id: organization_id,
    _product: product_data
  });

  if (error) {
    console.error('Error creating product and casepack:', error);
    return { data: null, error };
  }

  return { data, error: null };
};

// Update a single consumer product row
const updateConsumerProduct = async (productId, updateData) => {
  // Map fields for database compatibility
  const dbUpdateData = { ...updateData };
  if (dbUpdateData.name !== undefined) {
    dbUpdateData.product_name = dbUpdateData.name;
    delete dbUpdateData.name;
  }

  const { data, error } = await supabase
    .from('organization_products')
    .update(dbUpdateData)
    .eq('id', productId)
    .select();

  if (error) {
    console.error('Error updating consumer product:', error);
    return { data: null, error };
  }

  return { data, error: null };
};

// Update multiple consumer product rows
const updateMultipleConsumerProducts = async (productsData, organization_id) => {
  
  // Map fields for database compatibility
  const dbProductsData = productsData.map(product => {
    const dbProduct = { ...product };
    if (dbProduct.name !== undefined) {
      dbProduct.product_name = dbProduct.name;
      delete dbProduct.name;
    }
    // Ensure organization_id is included
    dbProduct.organization_id = organization_id;
    return dbProduct;
  });

  const { data, error } = await supabase
    .schema('public')
    .from('organization_products')
    .upsert(dbProductsData)
    .select();

  if (error) {
    console.error('Error updating multiple consumer products:', error);
    return { data: null, error };
  }

  return { data, error: null };
};

// Update default casepack for a consumer product
const updateDefaultCasepack = async (productId, defaultCasepackId) => {
  const { data, error } = await supabase
    .schema('public')
    .from('organization_products')
    .update({ default_shippable_product_id: defaultCasepackId })
    .eq('id', productId)
    .select();

  if (error) {
    console.error('Error updating default casepack:', error);
    return { data: null, error };
  }

  return { data, error: null };
};

// Insert a new casepack
const insertCasepack = async (casepackData, organization_id) => {
  // Ensure organization_id is included in the casepack data
  const casepackDataWithOrg = {
    ...casepackData,
    organization_id: organization_id
  };

  const { data, error } = await supabase
    .schema('public')
    .from('organization_shippable_products')
    .insert([casepackDataWithOrg])
    .select();

  if (error) {
    console.error('Error inserting casepack:', error);
    return { data: null, error };
  }

  return { data, error: null };
};

// Update a single casepack
const updateCasepack = async (casepackId, updateData) => {
  const { data, error } = await supabase
    .schema('public')
    .from('organization_shippable_products')
    .update(updateData)
    .eq('id', casepackId)
    .select();

  if (error) {
    console.error('Error updating casepack:', error);
    return { data: null, error };
  }

  return { data, error: null };
};

// Insert a new shipper
const insertShipper = async (shipperData) => {
  const { data, error } = await supabase
    .schema('public')
    .from('organization_shippable_products')
    .insert([shipperData])
    .select();

  if (error) {
    console.error('Error inserting shipper:', error);
    return { data: null, error };
  }

  return { data, error: null };
};

// Update an existing shipper
const updateShipper = async (shipperId, updateData) => {
  const { data, error } = await supabase
    .schema('public')
    .from('organization_shippable_products')
    .update(updateData)
    .eq('id', shipperId)
    .select();

  if (error) {
    console.error('Error updating shipper:', error);
    return { data: null, error };
  }

  return { data, error: null };
};

const getIntegrationProductsV2 = async (organization_id, integration_id) => {
  const { data, error } = await supabase.schema('frontend').rpc('get_integration_products_v2', {
    _org_id: organization_id,
    _integration_id: integration_id,
  });

  if (error) {
    console.error('Error getting org products info:', error);
    return;
  }

  return data;
};

const updateIntegrationProductMapping = async (organization_id, integration_id, product_mapping_key, consumer_product_id, shippable_product_id, represents_shippable, ignore = false) => {
  const { data, error } = await supabase.schema('frontend').rpc('update_integration_product_mapping_v2', {
    _org_id: organization_id,
    _integration_id: integration_id,
    _product_mapping_key: product_mapping_key,
    _consumer_product_id: consumer_product_id,
    _shippable_product_id: shippable_product_id,
    _represents_shippable: represents_shippable,
    _ignore: ignore,
  });

  if (error) {
    console.error('Error updating integration product mapping:', error);
    return;
  }
  return data;
};

const getConnectionModelSummary = async (connection_id) => {
  const { data, error } = await supabase.rpc(
    'get_connection_external_model_summary_v1',
    { _connection_id: connection_id }
  );

  if (error) {
    console.error('Error getting connection info:', error);
    return;
  }

  return data;
};

const getProfile = async () => {
  const { data, error } = await supabase
    .schema('frontend')
    .rpc('get_profile_v2');
  if (error) {
    console.error('Error fetching data:', error);
    throw error;
  }
  const profileData = data;
  return profileData;
};

const getEnrichedConnections = async (currentOrganization) => {
  const { data, error } = await supabase
    .schema('frontend')
    .rpc('get_enriched_connections_v3', {
      _organization_id: currentOrganization,
    });

  if (!data || error) {
      return null;
    }

  return data;
};

const getEnrichedConnectionv3 = async (currentOrganization, integration_id) => {
  const { data, error } = await supabase
    .schema('frontend')
    .rpc('get_enriched_connection_v3', {
      _organization_id: currentOrganization,
      _integration_id: integration_id,
    });
  if (!data || error) {
    return null;
  }

  return data;
};

const getOnboardingConfiguration = async (connection_id) => {
  const { data, error } = await supabase
    .schema('frontend')
    .rpc('get_onboarding_configuration', {
      _connection_id: connection_id,
    });
  if (error) {
    console.error('Error fetching onboarding configuration:', error);
    return null;
  }
  return data;
};


const getEmbeddedAnalyticsUrl = async (organization_id, dashboard_id) => {
  const { data, error } = await supabase.rpc('generate_analytics_embed_url', {
    _organization_id: organization_id,
    _dashboard_id: dashboard_id,
  });

  if (error) {
    console.error('Error getting embeded url:', error);
    return;
  }

  return data;
};

const submitOnboardingStep = async (stepId, formData, schema = null) => {
  if (formData) {
    const properties = schema?.properties ?? {};
    const passwordKeys = new Set(
      Object.keys(properties).filter(
        (key) => properties[key]?.format === 'password' || key === 'password'
      )
    );
    // Backwards compatibility when schema is not provided
    if (passwordKeys.size === 0 && formData.password) {
      passwordKeys.add('password');
    }

    for (const key of passwordKeys) {
      if (formData[key]) {
        formData[key] = await encryptWithPublicKey(formData[key]);
      }
    }
  }

  const { data, error } = await supabase
    .schema('frontend')
    .rpc('submit_onboarding_step', {
      _form_values: formData || null,
      _id: stepId,
    });
  if (error) {
    console.error('error submitting Step: ', error);
  }
  if (data?.success) {
    return data.onboarding_configuration;
  }
};

const resetOnboardingStep = async (stepId) => {
  const { data, error } = await supabase
    .schema('frontend')
    .rpc('reset_onboarding_step', {
      _id: stepId,
    });
  if (error) {
    console.error('error submitting Step: ', error);
  }
  if (data?.success) {
    return data.onboarding_configuration;
  }
};

const setShopifyStore = async (organization_id, shop) => {
  const { data, error } = await supabase
    .schema('frontend')
    .rpc('set_shopify_store', {
      _org_id: organization_id,
      _shop: shop,
    });
    return { data, error };
};

const activateShopifyStore = async (organization_id, shop) => {
  const { data, error } = await supabase
    .schema('frontend')
    .rpc('activate_shopify_store', {
      _org_id: organization_id,
      _shop: shop,
    });
    return { data, error };
};

const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  if (error) {
    console.error('error signing out:', error);
  }
  return { error };
};

const activateHgConnection = async (connection_id) => {
  const { data, error } = await supabase.schema('frontend').rpc('activate_hg_connection_v1', { _connection_id: connection_id });
  if (error) {
    console.error('Error activating HG connection', error);
  }
  return { data, error };
};

const resetPassword = async (email) => {
  const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: 'https://app.muffindata.com/set-password',
  });
  return { data, error };
};

const signInWithOTP = async (email) => {
  const { data, error } = await supabase.auth.signInWithOtp({
    email: email,
    options: {
      emailRedirectTo: 'https://app.muffindata.com/set-password',
    },
  });
  return { data, error };
};

const updateUserPassword = async (password) => {
  const { data, error } = await supabase.auth.updateUser({
    password: password,
  });
  return { data, error };
};

const signInWithPassword = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email: email,
    password: password,
  });
  return { data, error };
};

const verifyOtp = async (token, type) => {
  const { data, error } = await supabase.auth.verifyOtp({
    token_hash: token,
    type: type,
  });
  return { data, error };
};

const getEnrichedOrganization = async (organization_id) => {
  const { data, error } = await supabase.schema('frontend').rpc('get_enriched_organization_v3', { _organization_id: organization_id });
  return { data, error };
};

const listOrganizationProfilesV1 = async (organization_id) => {
  const { data, error } = await supabase.schema('frontend').rpc('list_organization_profiles_v1', {
    _organization_id: organization_id,
  });
  return { data, error };
};

const bulkUpsertUsersV1 = async (organization_id, invitees) => {
  const { data, error } = await supabase.schema('frontend').rpc('bulk_upsert_users_v1', {
    _organization_id: organization_id,
    _invitees: invitees,
  });
  return { data, error };
};

const generateHgJwt = async () => {
  const { data, error } = await supabase.functions.invoke('generate_hg_jwt');
  return { data, error };
};

const generateMbJwt = async (organization_id) => {
  const { data, error } = await supabase.schema('frontend').rpc('get_mb_jwt_v1', { _organization_id: organization_id });
  return { data, error };
};

const setMbHomepage = async (user_id, analytics_homepage) => {
  const { error } = await supabase
    .from('profiles')
    .update({ analytics_homepage: analytics_homepage })
    .eq('id', user_id);
  return { error };
};

const getSession = async () => {
  const {
    data: { session },
    error,
  } = await supabase.auth.getSession();
  return { session , error };
};

const subscribeToAuthStateChange = (callback) => {
  const {
    data: { subscription },
  } = supabase.auth.onAuthStateChange(callback);
  return subscription;
};

const deleteCasepackV1 = async (casepackId) => {
  const { data, error } = await supabase.schema('frontend').rpc('delete_casepack_v1', { _casepack_id: casepackId });
  if (error) {
    console.error('Error deleting casepack:', error);
    return false;
  }
  return data;
};

export {
  getProfile,
  getEnrichedConnections,
  getOnboardingConfiguration,
  getEnrichedConnectionv3,
  getConnectionModelSummary,
  getIntegrationProductsV2,
  getEmbeddedAnalyticsUrl,
  getSubscription,
  updateDefaultOrg,
  getPreSignedS3Url,
  getFileUploadHistory,
  submitOnboardingStep,
  resetOnboardingStep,
  getOrganizationProducts,
  createProductAndCasepack,
  updateConsumerProduct,
  updateMultipleConsumerProducts,
  updateDefaultCasepack,
  insertCasepack,
  updateCasepack,
  insertShipper,
  updateShipper,
  updateIntegrationProductMapping,
  setShopifyStore,
  activateShopifyStore,
  signOut,
  activateHgConnection,
  resetPassword,
  signInWithOTP,
  updateUserPassword,
  signInWithPassword,
  verifyOtp,
  getEnrichedOrganization,
  listOrganizationProfilesV1,
  bulkUpsertUsersV1,
  generateHgJwt,
  generateMbJwt,
  setMbHomepage,
  getSession,
  subscribeToAuthStateChange,
  deleteCasepackV1,
};
